import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import {
  AnswerKey,
  AppState,
  FitAssistantBikesData,
  FitStatus,
  FlowAction,
  FlowOption,
  FlowState,
  NodeId,
} from './Types/Types.ts';
import { FLOW_START_NODE, getFlowNode } from './Flow.ts';
import { loadPanniersAnswers, removePanniersAnswers, savePanniersAnswers } from './Helpers/CookieHelper.ts';
import { IsProductPage } from '../FitAssistantShared/IsProductPage.js';
import { ResetFitStatusForAllProducts, SetFitStatusForAllProducts } from '../FitAssistantShared/Functions.js';

const AppStateContext = createContext({} as AppState);

const initialFlowState: FlowState = {
  currentNodeId: FLOW_START_NODE,
  history: [],
  answers: {},
};

const clearFutureAnswers = (state: FlowState, keysToClear: AnswerKey[]): FlowState => {
  if (!keysToClear.length) {
    return state;
  }
  const answers = { ...state.answers };
  keysToClear.forEach((key) => {
    delete answers[key];
  });
  return { ...state, answers };
};

const reducer = (state: FlowState, action: FlowAction): FlowState => {
  if (action.type === 'reset') {
    return { ...initialFlowState };
  }

  if (action.type === 'restore') {
    return action.flowState;
  }

  if (action.type === 'back') {
    if (state.history.length === 0) {
      return state;
    }
    const currentNode = getFlowNode(state.currentNodeId);
    const previousNode = state.history[state.history.length - 1];
    const previousFlowNode = getFlowNode(previousNode);
    const nextState = {
      ...state,
      currentNodeId: previousNode,
      history: state.history.slice(0, -1),
    };

    const keysToClear: AnswerKey[] = [];
    if (currentNode.answerKey) {
      keysToClear.push(currentNode.answerKey);
    }
    if (previousFlowNode.answerKey) {
      keysToClear.push(previousFlowNode.answerKey);
    }

    if (!keysToClear.length) {
      return nextState;
    }

    return clearFutureAnswers(nextState, [...new Set(keysToClear)]);
  }

  const currentNode = getFlowNode(state.currentNodeId);
  if (
    (currentNode.kind !== 'choice' && currentNode.kind !== 'product') ||
    !currentNode.answerKey ||
    !currentNode.getNext
  ) {
    return state;
  }

  const nextAnswers = { ...state.answers, [currentNode.answerKey]: action.option.value };
  const nextNode = action.option.nextNodeId
    ? action.option.nextNodeId
    : currentNode.getNext({ answers: nextAnswers }, action.option.value);

  return {
    ...state,
    currentNodeId: nextNode,
    history: [...state.history, state.currentNodeId],
    answers: nextAnswers,
  };
};

const AppStateProvider = ({ children }: PropsWithChildren) => {
  const [showFitAssistant, setShowFitAssistant] = useState(false);
  const [flowState, dispatch] = useReducer(reducer, initialFlowState);
  // _THULEDATA.FitAssistantBikes is a shared, polymorphic key: the backend fills it with whichever
  // fit-assistant payload matches the page. It's typed globally as the BikeAdapters shape, so cast to
  // the Panniers shape that this app is actually served.
  const [fitAssistantBikesData] = useState<FitAssistantBikesData | null>(
    (window._THULEDATA?.FitAssistantBikes as unknown as FitAssistantBikesData) ?? null
  );
  const [fitStatus, setFitStatus] = useState<FitStatus>('noselection');

  useEffect(() => {
    const savedAnswers = loadPanniersAnswers();
    if (!savedAnswers || Object.keys(savedAnswers).length === 0) return;

    let currentNodeId: NodeId = FLOW_START_NODE;
    const history: NodeId[] = [];

    while (true) {
      const node = getFlowNode(currentNodeId);
      if (node.kind === 'result' || !node.answerKey || !node.getNext) break;

      const answer = savedAnswers[node.answerKey];
      if (answer === undefined || answer === null) break;

      const nextNodeId = node.getNext({ answers: savedAnswers }, answer);
      history.push(currentNodeId);
      currentNodeId = nextNodeId;
    }

    dispatch({ type: 'restore', flowState: { currentNodeId, history, answers: savedAnswers } });
  }, []);

  useEffect(() => {
    const currentNode = getFlowNode(flowState.currentNodeId);
    if (currentNode.kind === 'product') {
      setFitStatus('confirmed');
      savePanniersAnswers(flowState.answers);
      if (!IsProductPage) SetFitStatusForAllProducts('confirmed');
    } else if (flowState.currentNodeId === 'nofit') {
      setFitStatus('nofit');
      savePanniersAnswers(flowState.answers);
      if (!IsProductPage) SetFitStatusForAllProducts('nofit');
    }
  }, [flowState.currentNodeId]);

  const selectOption = useCallback((option: FlowOption) => {
    dispatch({ type: 'select', option });
  }, []);

  const goBack = useCallback(() => {
    const currentNode = getFlowNode(flowState.currentNodeId);
    if (currentNode.kind === 'product' || currentNode.kind === 'result') {
      setFitStatus('noselection');
      removePanniersAnswers();
      if (!IsProductPage) ResetFitStatusForAllProducts(false);
    }
    dispatch({ type: 'back' });
  }, [flowState.currentNodeId]);

  const resetFlow = useCallback(() => {
    dispatch({ type: 'reset' });
    setFitStatus('noselection');
    removePanniersAnswers();
    if (!IsProductPage) ResetFitStatusForAllProducts(false);
  }, []);

  const value: AppState = {
    showFitAssistant,
    setShowFitAssistant,
    currentNodeId: flowState.currentNodeId,
    selectOption,
    goBack,
    resetFlow,
    canGoBack: flowState.history.length > 0,
    answers: flowState.answers,
    fitAssistantBikesData,
    fitStatus,
  };

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
};

const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};

export { AppStateProvider, useAppState };
